import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { L3_PATHS } from 'Deer/constants/routes';
import { sendRequest } from 'api/sendRequest';
import { useToast } from 'components/hooks/useToast';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import type {
  SurveySettings,
  SurveyFormResponse,
  SurveyTemplate,
  SelectedSurveyTemplateResponse,
  SurveyRemindersResponse,
} from './types';

export const useSurveyLastSavedTime = () => {
  const queryClient = useQueryClient();

  // Query to get the last saved time
  const { data: lastSavedTime } = useQuery<string>({
    queryKey: [REACT_QUERY_KEYS.engagement.surveyCreation.getSurveyLastSavedTime],
    queryFn: () => queryClient.getQueryData([REACT_QUERY_KEYS.engagement.surveyCreation.getSurveyLastSavedTime]),
    initialData: null,
  });

  // Function to update the last saved time
  const updateLastSavedTime = () => {
    const now = new Date();
    const formattedTime = format(now, 'h:mma');
    queryClient.setQueryData([REACT_QUERY_KEYS.engagement.surveyCreation.getSurveyLastSavedTime], formattedTime);
    return formattedTime;
  };

  return { lastSavedTime, updateLastSavedTime };
};

const createNewSurveyForm = async (): Promise<SurveyFormResponse> => {
  try {
    const url = endpoints.engagementV2.surveyCreation.createForm;
    const response = await sendRequest({ url, method: 'POST', credentials: 'include' });
    return response.entity;
  } catch (e) {
    console.error('Error failed to create new survey form', e);
    throw e;
  }
};

export const useCreateNewSurveyForm = ({ selectedSurveyCreationPreset = undefined } = {}) => {
  const history = useHistory();
  const toast = useToast();

  return useMutation({
    mutationFn: createNewSurveyForm,
    onSuccess: (data) => {
      // temporary FE handle survey preset code
      const queryParams = new URLSearchParams();
      if (selectedSurveyCreationPreset) {
        queryParams.append('preset', selectedSurveyCreationPreset);
      }
      const queryParamsString = queryParams.toString();
      history.push(
        `${L3_PATHS.company.engagement.surveyCreation.replace(':surveyId', String(data.id))}${
          queryParamsString ? `?${queryParamsString}` : ''
        }`
      );
      // history.push(L3_PATHS.company.engagement.surveyCreation.replace(':surveyId', String(data.id)));
    },
    onError: () => {
      toast({ title: 'New survey form creation failed, please try again', status: 'error' });
    },
  });
};

const getSurveyForm = async (surveyId: string): Promise<SurveyFormResponse> => {
  try {
    const url = endpoints.engagementV2.surveyCreation.getForm;
    const response = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
      queryParams: { surveyId },
    });
    return response.entity;
  } catch (e) {
    console.error('Error failed to get survey form', e);
    throw e;
  }
};

export const useGetSurveyForm = (surveyId: string, enabled: boolean = true) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.engagement.surveyCreation.getSurveyForm, surveyId],
    queryFn: () => getSurveyForm(surveyId),
    enabled: !!surveyId && enabled,
  });

const getSurveySettings = async (surveyId: string): Promise<SurveySettings> => {
  try {
    const url = endpoints.engagementV2.surveyCreation.getSurveySettings;
    const response = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
      queryParams: { surveyId },
    });
    return response.entity as SurveySettings;
  } catch (e) {
    console.error('Error failed to get survey settings', e);
    throw e;
  }
};

export const useGetSurveySettings = (surveyId: string, enabled: boolean = true) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.engagement.surveyCreation.getSurveySettings, surveyId],
    queryFn: () => getSurveySettings(surveyId),
    enabled: !!surveyId && enabled,
  });

const updateSurveyForm = async ({
  surveyId,
  updatedFormFields,
}: {
  surveyId: string;
  updatedFormFields: Partial<SurveyFormResponse>;
}) => {
  try {
    const url = endpoints.engagementV2.surveyCreation.updateForm;
    const response = await sendRequest({
      url,
      method: 'PUT',
      credentials: 'include',
      queryParams: { surveyId },
      body: { id: surveyId, ...updatedFormFields },
    });
    return response;
  } catch (e) {
    console.error('Error failed to update survey form', e);
    throw e;
  }
};

export const useUpdateSurveyForm = (surveyId: string) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { updateLastSavedTime } = useSurveyLastSavedTime();

  return useMutation({
    mutationFn: updateSurveyForm,
    onSuccess: () => {
      updateLastSavedTime();
      queryClient.invalidateQueries([REACT_QUERY_KEYS.engagement.surveyCreation.getSurveyForm, surveyId]);
    },
    onError: () => {
      toast({ title: 'Survey form update failed, please try again', status: 'error' });
    },
  });
};

const updateSurveySettings = async ({
  surveyId,
  updatedFormFields,
}: {
  surveyId: string;
  updatedFormFields: Partial<SurveySettings>;
}) => {
  try {
    const url = endpoints.engagementV2.surveyCreation.updateSurveySettings;
    const response = await sendRequest({
      url,
      method: 'PUT',
      credentials: 'include',
      queryParams: { surveyId },
      body: updatedFormFields,
    });
    return response;
  } catch (e) {
    console.error('Error failed to update survey settings', e);
    throw e;
  }
};

export const useUpdateSurveySettings = (surveyId: string) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { updateLastSavedTime } = useSurveyLastSavedTime();
  const previousData: SurveySettings = queryClient.getQueryData([
    REACT_QUERY_KEYS.engagement.surveyCreation.getSurveySettings,
    surveyId,
  ]);

  const { startDate, endDate, visibilityType } = previousData;
  const oldData = { startDate, endDate, visibilityType };

  return useMutation({
    // eslint-disable-next-line no-shadow
    mutationFn: ({ surveyId, updatedFormFields }: { surveyId: string; updatedFormFields: Partial<SurveySettings> }) =>
      updateSurveySettings({ surveyId, updatedFormFields: { ...oldData, ...updatedFormFields } }),
    onSuccess: () => {
      updateLastSavedTime();
      queryClient.invalidateQueries([REACT_QUERY_KEYS.engagement.surveyCreation.getSurveySettings, surveyId]);
    },
    onError: () => {
      toast({ title: 'Survey settings update failed, please try again', status: 'error' });
    },
  });
};

const getSurveyTemplateList = async (): Promise<SurveyTemplate[]> => {
  try {
    const url = endpoints.engagementV2.surveyCreation.getTemplateList;
    const response = await sendRequest({ url, method: 'GET', credentials: 'include' });
    return response.entity;
  } catch (e) {
    console.error('Error failed to get survey template list', e);
    throw e;
  }
};

export const useGetSurveyTemplateList = () =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.engagement.surveyCreation.getSurveyTemplateList],
    queryFn: getSurveyTemplateList,
  });

const getSurveySelectedTemplate = async (surveyId: string): Promise<SelectedSurveyTemplateResponse> => {
  try {
    const url = endpoints.engagementV2.surveyCreation.getSelectedTemplate;
    const response = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
      queryParams: { surveyId },
    });
    return response;
  } catch (e) {
    console.error('Error failed to get survey selected template', e);
    throw e;
  }
};

export const useGetSurveySelectedTemplate = (surveyId: string, enabled: boolean = true) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.engagement.surveyCreation.getSurveySelectedTemplate, surveyId],
    queryFn: () => getSurveySelectedTemplate(surveyId),
    retry: (failureCount, error) => {
      if (failureCount > 2) return false;
      // BE returns 409 with status as CONFLICT if template is not tagged to a survey
      if ((error as { body?: { status?: string } })?.body?.status === 'CONFLICT') return false; // template not tagged to a survey
      return true;
    },
    enabled: !!surveyId && enabled,
  });

const updateSurveySelectedTemplate = async ({ surveyId, templateId }: { surveyId: string; templateId: number }) => {
  try {
    const url = endpoints.engagementV2.surveyCreation.updateSelectedTemplate;
    const response = await sendRequest({
      url,
      method: 'PUT',
      credentials: 'include',
      queryParams: { surveyId, templateId },
    });
    return response;
  } catch (e) {
    console.error('Error failed to update survey selected template', e);
    throw e;
  }
};

export const useUpdateSurveySelectedTemplate = () => {
  const toast = useToast();
  const { updateLastSavedTime } = useSurveyLastSavedTime();
  return useMutation({
    mutationFn: ({ surveyId, templateId }: { surveyId: string; templateId: number }) =>
      updateSurveySelectedTemplate({ surveyId, templateId }),
    onSuccess: () => {
      updateLastSavedTime();
    },
    onError: () => {
      toast({ title: 'Error updating template', status: 'error' });
    },
  });
};

const getSurveyReminders = async (surveyId: string): Promise<SurveyRemindersResponse> => {
  try {
    const url = endpoints.engagementV2.surveyCreation.getReminders;
    const response = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
      queryParams: { surveyId },
    });
    return response.entity;
  } catch (e) {
    console.error('Error failed to get survey reminders', e);
    throw e;
  }
};

export const useGetSurveyReminders = (surveyId: string) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.engagement.surveyCreation.getSurveyReminders, surveyId],
    queryFn: () => getSurveyReminders(surveyId),
  });

const updateSurveyReminders = async ({
  surveyId,
  reminders,
}: {
  surveyId: string;
  reminders: SurveyRemindersResponse;
}) => {
  try {
    const url = endpoints.engagementV2.surveyCreation.updateReminders;
    const response = await sendRequest({
      url,
      method: 'PUT',
      credentials: 'include',
      queryParams: { surveyId },
      body: reminders,
    });
    return response;
  } catch (e) {
    console.error('Error failed to update survey reminders', e);
    throw e;
  }
};

export const useUpdateSurveyReminders = () => {
  const toast = useToast();
  const { updateLastSavedTime } = useSurveyLastSavedTime();
  return useMutation({
    mutationFn: ({ surveyId, reminders }: { surveyId: string; reminders: SurveyRemindersResponse }) =>
      updateSurveyReminders({ surveyId, reminders }),
    onSuccess: () => {
      updateLastSavedTime();
    },
    onError: () => {
      toast({ title: 'Error updating reminders', status: 'error' });
    },
  });
};

const launchSurvey = async (surveyId: string) => {
  try {
    const url = endpoints.engagementV2.surveyCreation.launchSurvey;
    const response = await sendRequest({
      url,
      method: 'POST',
      credentials: 'include',
      queryParams: { surveyId },
    });
    return response;
  } catch (e) {
    console.error('Error failed to launch survey', e);
    throw e;
  }
};

export const useLaunchSurvey = (surveyId: string) => {
  const toast = useToast();
  const history = useHistory();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => launchSurvey(surveyId),
    onSuccess: () => {
      toast({ title: 'Survey launched successfully', status: 'success' });
      history.push(`${L3_PATHS.company.engagement.settings}?activeTabId=engagement_surveys`);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.engagement.surveyCreation.getSurveySettings, surveyId]);
    },
    onError: () => {
      toast({ title: 'Error launching survey', status: 'error' });
    },
  });
};
